import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SidebarModule } from 'primeng/sidebar';
import { ButtonModule } from 'primeng/button';
import { Category } from '../../../models/category';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { NgIf } from '@angular/common';
import { CardModule } from 'primeng/card';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CategoryStore } from '../../../stores/category/category.state';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { DividerModule } from 'primeng/divider';
import { AppUtilityService } from '../../../utility/app.utility';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { PermissionService } from '../../../services/permission.service';
import { TreeSelectModule } from 'primeng/treeselect';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { DebugComponent } from '../../atoms/debug/debug.component';

@Component({
  selector: 'app-category-form',
  standalone: true,
  imports: [
    SidebarModule,
    ButtonModule,
    ReactiveFormsModule,
    CheckboxModule,
    NgIf,
    CardModule,
    AutoCompleteModule,
    InputTextModule,
    InputTextareaModule,
    MultiSelectModule,
    DropdownModule,
    FormFieldWrapperComponent,
    DividerModule,
    TreeSelectModule,
    InputSwitchModule,
    FormFooterComponent,
    WorkspaceDropdownComponent,
    DebugComponent,
  ],
  templateUrl: './category-form.component.html',
  styleUrl: './category-form.component.scss',
})
export class CategoryFormComponent extends BaseFormComponent<Category> {
  readonly store = inject(CategoryStore);
  override formInitialized = false;
  override form: FormGroup = this.fb.group({});
  override itemToEdit?: Category;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
  }

  /**
   * Initializes the reactive form with predefined structure and values.
   * Sets the initial values for form controls based on the provided item to edit or query parameters.
   * Configures validation rules for certain controls.
   *
   * @return {void} This method does not return any value.
   */
  initializeForm(): void {
    const parentFromQueryParams =
      this.routingService.getQueryParamFromRoute('parentId');
    this.form = this.fb.group({
      workspaces: [this.itemToEdit?.workspaces || []],
      name: [this.itemToEdit?.name || '', Validators.required],
      subtitle: [this.itemToEdit?.subtitle || ''],
      description: [this.itemToEdit?.description || ''],
      parent: [
        parentFromQueryParams
          ? this.store.getByIdAsTreeItem(parentFromQueryParams)
          : this.itemToEdit && this.itemToEdit.parent
            ? this.store.getByIdAsTreeItem(this.itemToEdit.parent)
            : '',
      ],
      locked: [this.itemToEdit?.locked || false, Validators.required],
    });

    this.formInitialized = true;
  }

  /**
   * Removes a specified category after confirmation.
   * This method triggers a confirmation dialog for category deletion,
   * and upon confirmation, executes the removal process.
   *
   * @param {Category} item - The category object to be removed.
   * @return {void} This method does not return any value.
   */
  removeCategory(item: Category): void {
    this.confirmationDialogService.confirmCategoryDeletion(
      undefined,
      item,
      () => this.remove(item)
    );
  }

  /**
   * Handles the submission of the form by modifying the form value and invoking the submit method.
   *
   * The method overrides the current form value by extracting it and adding a modified reference
   * to the parent property with its unique identifier (_id). The updated object is then passed
   * to the submit function for further processing.
   *
   * @return {void} Does not return any value.
   */
  onSubmit(): void {
    const overrideFormValue = this.form.value;
    overrideFormValue.parent = this.form.value.parent._id;
    this.submit(overrideFormValue);
  }
}
